// src/features/locker-controllers/hooks/useLockerControllerPermissions.ts

import { useMemo } from 'react';
import { useResourcePermissions } from '@/hooks/useAppPermissions'; // Importa el hook base
import { LockerControllerListItem } from '../types';

export interface UseLockerControllerPermissionsResult {
  // Permisos básicos
  canViewLockerControllers: boolean;
  canCreateLockerControllers: boolean;
  canEditLockerControllers: boolean;
  canDeleteLockerControllers: boolean;

  // Permisos técnicos
  canConfigureNetwork: boolean;
  canManageConnections: boolean;
  canViewTechnicalDetails: boolean;

  // Permisos de negocio específicos (contextuales)
  canEditThisController: boolean;
  canDeleteThisController: boolean;
  canConfigureThisController: boolean;
  
  // Utilidades del sistema base
  hasRole: (...roles: string[]) => boolean;
  isAtLeastRole: (minRole: string) => boolean;
  currentRole: string | null;
}

/**
 * Hook específico para permisos del feature de Locker Controllers
 */
export const useLockerControllerPermissions = (controller?: LockerControllerListItem): UseLockerControllerPermissionsResult => {
  // 1. Usar el hook base de permisos, igual que en groups
  const permissions = useResourcePermissions('locker-controllers', controller);

  const controllerPermissions = useMemo<UseLockerControllerPermissionsResult>(() => {
    // ========================================
    // PERMISOS BÁSICOS (mapeados desde el hook base)
    // ========================================
    const canViewLockerControllers = permissions.canView;
    const canCreateLockerControllers = permissions.canAdd;
    const canEditLockerControllers = permissions.canModify;
    const canDeleteLockerControllers = permissions.canRemove;

    // ========================================
    // PERMISOS TÉCNICOS/EXTENDIDOS (acciones personalizadas)
    // ========================================
    const canConfigureNetwork = permissions.canPerform('locker-controllers', 'configureNetwork' as any);
    const canManageConnections = permissions.canPerform('locker-controllers', 'manageConnections' as any);
    const canViewTechnicalDetails = permissions.canPerform('locker-controllers', 'viewTechnicalDetails' as any);

    // ========================================
    // PERMISOS DE NEGOCIO (lógica contextual)
    // ========================================

    // Permisos que dependen de un 'controller' específico
    const canEditThisController = controller ? permissions.canEditThis(controller) : canEditLockerControllers;
    
    // La lógica de negocio se mantiene, pero se combina con el permiso base
    const canDeleteThisController = (() => {
      if (!controller) {
        return canDeleteLockerControllers; // Permiso general si no hay un controller específico
      }
      
      // Primero, verificar si el rol tiene permiso para eliminar
      if (!permissions.canDeleteThis(controller)) {
        return false;
      }

      // Segundo, aplicar la regla de negocio (que no esté online)
      if (controller.lastSeenAt) {
        const lastSeenDate = new Date(controller.lastSeenAt);
        const now = new Date();
        const diffInMinutes = (now.getTime() - lastSeenDate.getTime()) / 60000;
        
        if (diffInMinutes < 30) {
          return false; // Está online, no se puede borrar
        }
      }
      
      return true; // Pasa ambas verificaciones
    })();
    
    const canConfigureThisController = canConfigureNetwork && canEditThisController;

    // ========================================
    // RETORNAR OBJETO COMPLETO
    // ========================================
    return {
      canViewLockerControllers,
      canCreateLockerControllers,
      canEditLockerControllers,
      canDeleteLockerControllers,
      canConfigureNetwork,
      canManageConnections,
      canViewTechnicalDetails,
      canEditThisController,
      canDeleteThisController,
      canConfigureThisController,
      // Re-exportar utilidades del hook base
      hasRole: permissions.hasRole,
      isAtLeastRole: permissions.isAtLeastRole,
      currentRole: permissions.role,
    };
  }, [permissions, controller]);

  return controllerPermissions;
};

// ¡No olvides el helper para listas!
/**
 * Hook para verificar permisos sobre múltiples controllers
 */
export const useLockerControllersListPermissions = (controllers: LockerControllerListItem[] = []) => {
    // Implementación similar a useGroupsListPermissions...
    // Esto es un ejercicio para ti, pero sería casi idéntico.
};


export default useLockerControllerPermissions;